import { Fragment, type ReactNode } from 'react';

/**
 * Minimal, dependency-free renderer for the constrained markdown our meeting summaries emit:
 * `##`/`###` headers, `-`/`*` bullets (with one level of nesting), `**bold**`, and blank-line
 * paragraphs. Input is our own trusted LLM output and React escapes all text, so no sanitiser is
 * needed. This is NOT a general-purpose markdown engine - it only covers what the summary prompt
 * produces (see transcript-insights.ts longSummary rules).
 */
export function MarkdownLite({ text, className }: { text: string; className?: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let bullets: { depth: number; content: string }[] = [];
  let k = 0;

  const flushBullets = () => {
    if (bullets.length === 0) return;
    const items = bullets;
    bullets = [];
    blocks.push(
      <ul key={`ul-${k++}`} className="my-1.5 space-y-1">
        {items.map((b, i) => (
          <li key={i} className="list-disc text-foreground/90" style={{ marginLeft: `${1 + b.depth}rem` }}>
            {renderInline(b.content)}
          </li>
        ))}
      </ul>,
    );
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    if (!line.trim()) {
      flushBullets();
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      flushBullets();
      const level = h[1].length;
      const cls =
        level <= 2
          ? 'mt-4 mb-1 text-sm font-semibold text-foreground first:mt-0'
          : 'mt-3 mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground';
      blocks.push(
        <p key={`h-${k++}`} className={cls}>
          {renderInline(h[2])}
        </p>,
      );
      continue;
    }
    const b = /^(\s*)[-*]\s+(.*)$/.exec(line);
    if (b) {
      const indent = b[1].replace(/\t/g, '  ').length;
      bullets.push({ depth: Math.min(2, Math.floor(indent / 2)), content: b[2] });
      continue;
    }
    flushBullets();
    blocks.push(
      <p key={`p-${k++}`} className="my-1.5 leading-relaxed text-foreground/90">
        {renderInline(line)}
      </p>,
    );
  }
  flushBullets();

  return <div className={className}>{blocks}</div>;
}

/** Inline formatting: `**bold**` only; everything else is literal (React escapes it). */
function renderInline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    /^\*\*[^*]+\*\*$/.test(p) ? <strong key={i}>{p.slice(2, -2)}</strong> : <Fragment key={i}>{p}</Fragment>,
  );
}
